import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useListInventoryCategories,
  useCreateInventoryCategory,
  useUpdateInventoryCategory,
  useDeleteInventoryCategory,
  getListInventoryCategoriesQueryKey,
  useListInventoryItems,
} from "@workspace/api-client-react";
import type { InventoryCategory } from "@workspace/api-client-react";

// Phase 2.1 polish C: categories are flat — (name × charging_model) is the row identity.
// Add mode: user types one name + checks N models → server gets N POSTs (one per model).
// Edit mode: each row = one (name, charging_model) pair, so single-model select.

const CHARGING_MODELS = [
  "Complimentary_Unlimited",
  "Complimentary_With_Limit",
  "Pay_Per_Use",
  "Plan_Inclusive_Quota",
  "AddOn_Optional",
  "Internal_Use",
] as const;
type ChargingModel = (typeof CHARGING_MODELS)[number];

// 2026-05-18: "Allow recipe items" gate removed from Category UI. Any item can be a
// recipe now (gated only by Is Recipe toggle in Item form). The DB column lives on
// but every new/edited category writes allowsRecipe=true so legacy item-tab gating
// is effectively neutralized.
const CreateForm = z.object({
  name: z.string().min(1, "Category name is required"),
  chargingModels: z.array(z.enum(CHARGING_MODELS)).min(1, "Pick at least one usage model"),
  budgetMonthly: z.number().nullable().optional(),
  primaryResponsibleStaff: z.string().nullable().optional(),
  status: z.enum(["Active", "Archived"]).optional(),
  notes: z.string().nullable().optional(),
});

const EditForm = z.object({
  name: z.string().min(1, "Category name is required"),
  chargingModel: z.enum(CHARGING_MODELS),
  budgetMonthly: z.number().nullable().optional(),
  primaryResponsibleStaff: z.string().nullable().optional(),
  status: z.enum(["Active", "Archived"]).optional(),
  notes: z.string().nullable().optional(),
});

type CreateValues = z.infer<typeof CreateForm>;
type EditValues = z.infer<typeof EditForm>;

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { catId } from "@/lib/display-id";
import { CheckSquare, Trash2, EyeOff, Tag, X, History as HistoryIcon } from "lucide-react";
import { AuditHistoryDialog } from "./audit-history-dialog";

interface Props {
  openCreate: boolean;
  setOpenCreate: (open: boolean) => void;
}

const EMPTY_CREATE: CreateValues = {
  name: "",
  chargingModels: [],
  budgetMonthly: null,
  primaryResponsibleStaff: null,
  status: "Active",
  notes: null,
};

export function CategoriesTab({ openCreate, setOpenCreate }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryCategory | null>(null);
  const [historyTarget, setHistoryTarget] = useState<InventoryCategory | null>(null);
  const [search, setSearch] = useState("");

  const { data: categories, isLoading } = useListInventoryCategories();
  const { data: items } = useListInventoryItems();
  const deleteMutation = useDeleteInventoryCategory();

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteTarget.id });
      toast({ title: `Deleted category ${deleteTarget.name}.` });
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: getListInventoryCategoriesQueryKey() });
    } catch (err: unknown) {
      const status = (err as { status?: number; response?: { status?: number } })?.status
        ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast({
          title: `Category ${deleteTarget.name} has items.`,
          description: "Reassign these items to another category from the Items tab first, then retry the delete.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Delete failed. See console for details.", variant: "destructive" });
      }
    }
  }
  const filteredCategories = categories?.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.chargingModel.toLowerCase().includes(q);
  });

  const createMutation = useCreateInventoryCategory();
  const updateMutation = useUpdateInventoryCategory();

  // ---- Multi-select bulk actions ----
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<null | "delete" | "inactive">(null);
  const [bulkChargingModelOpen, setBulkChargingModelOpen] = useState(false);
  const [bulkChargingModel, setBulkChargingModel] = useState<ChargingModel>("Complimentary_Unlimited");
  const [bulkBusy, setBulkBusy] = useState(false);

  function toggleSelectMode() {
    setSelectMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  }
  function toggleRow(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAllVisible(checked: boolean) {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set((filteredCategories ?? []).map((c) => c.id)));
  }
  const visibleIds = (filteredCategories ?? []).map((c) => c.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  async function runBulkMutation(
    op: (id: number) => Promise<void>,
    successMessage: (n: number) => string,
  ) {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    let ok = 0;
    let fkBlocked = 0;
    let networkErr = 0;
    let otherErr = 0;
    for (const id of ids) {
      try {
        await op(id);
        ok++;
      } catch (err: unknown) {
        const status = (err as { status?: number; response?: { status?: number } })?.status
          ?? (err as { response?: { status?: number } })?.response?.status
          ?? 0;
        const message = String((err as { message?: string })?.message ?? "");
        if (status === 409) fkBlocked++;
        else if (status === 0 || message.includes("fetch") || message.includes("Network")) networkErr++;
        else otherErr++;
      }
    }
    queryClient.invalidateQueries({ queryKey: getListInventoryCategoriesQueryKey() });
    const totalFailed = fkBlocked + networkErr + otherErr;
    if (totalFailed === 0) {
      toast({ title: successMessage(ok) });
    } else if (networkErr === ids.length) {
      toast({ title: "API unreachable — is the server running?", variant: "destructive" });
    } else if (fkBlocked > 0 && networkErr === 0 && otherErr === 0) {
      toast({
        title: `${ok} succeeded · ${fkBlocked} have items`,
        description: "Categories with items cannot be deleted. Move or reassign their items to another category first, then retry.",
        variant: "destructive",
      });
    } else {
      const reasons: string[] = [];
      if (fkBlocked > 0) reasons.push(`${fkBlocked} have items`);
      if (networkErr > 0) reasons.push(`${networkErr} network error`);
      if (otherErr > 0) reasons.push(`${otherErr} other`);
      toast({
        title: `${ok} succeeded · ${reasons.join(" · ")}`,
        description: fkBlocked > 0 ? "Reassign items to another category before deleting." : undefined,
        variant: "destructive",
      });
    }
    setSelectedIds(new Set());
    setBulkBusy(false);
    setBulkConfirm(null);
    setBulkChargingModelOpen(false);
  }

  async function handleBulkDelete() {
    await runBulkMutation(
      (id) => deleteMutation.mutateAsync({ id }).then(() => undefined),
      (n) => `Deleted ${n} categories.`,
    );
  }
  async function handleBulkInactive() {
    await runBulkMutation(
      (id) => updateMutation.mutateAsync({ id, data: { status: "Archived" } }).then(() => undefined),
      (n) => `Marked ${n} categories as Archived.`,
    );
  }
  async function handleBulkChargingModel() {
    await runBulkMutation(
      (id) => updateMutation.mutateAsync({ id, data: { chargingModel: bulkChargingModel } }).then(() => undefined),
      (n) => `Updated charging model on ${n} categories.`,
    );
  }

  const createForm = useForm<CreateValues>({
    resolver: zodResolver(CreateForm),
    defaultValues: EMPTY_CREATE,
  });

  const editForm = useForm<EditValues>({
    resolver: zodResolver(EditForm),
    defaultValues: { name: "", chargingModel: "Complimentary_Unlimited" },
  });

  const isCreating = openCreate;
  const isEditing = editId !== null;
  const isOpen = isCreating || isEditing;

  function openEdit(cat: InventoryCategory) {
    setEditId(cat.id);
    editForm.reset({
      name: cat.name,
      chargingModel: cat.chargingModel as ChargingModel,
      budgetMonthly: cat.budgetMonthly ?? null,
      primaryResponsibleStaff: cat.primaryResponsibleStaff ?? null,
      status: (cat.status ?? "Active") as EditValues["status"],
      notes: cat.notes ?? null,
    });
  }

  function handleClose() {
    setOpenCreate(false);
    setEditId(null);
    createForm.reset(EMPTY_CREATE);
  }

  async function onCreate(values: CreateValues) {
    const base = {
      name: values.name,
      // Recipe gate retired in UI — always send true so item-tab "Is Recipe" toggle isn't blocked.
      allowsRecipe: true,
      budgetMonthly: values.budgetMonthly ?? null,
      primaryResponsibleStaff: values.primaryResponsibleStaff ?? null,
      status: values.status ?? "Active",
      notes: values.notes ?? null,
    };
    // Fan-out: one POST per checked model. Run sequentially so a duplicate (409) on one
    // model still creates the others, and the toast can report partial success.
    const results: { model: ChargingModel; ok: boolean }[] = [];
    for (const model of values.chargingModels) {
      try {
        await createMutation.mutateAsync({ data: { ...base, chargingModel: model } });
        results.push({ model, ok: true });
      } catch {
        results.push({ model, ok: false });
      }
    }
    queryClient.invalidateQueries({ queryKey: getListInventoryCategoriesQueryKey() });
    const okCount = results.filter((r) => r.ok).length;
    const failCount = results.length - okCount;
    if (failCount === 0) {
      toast({ title: `Created ${okCount} categor${okCount === 1 ? "y" : "ies"}.` });
      handleClose();
    } else if (okCount === 0) {
      toast({ title: "Could not create — names already exist for those models.", variant: "destructive" });
    } else {
      toast({
        title: `Created ${okCount}, ${failCount} skipped (duplicates).`,
        variant: "destructive",
      });
      handleClose();
    }
  }

  async function onEdit(values: EditValues) {
    if (editId === null) return;
    try {
      await updateMutation.mutateAsync({
        id: editId,
        data: {
          name: values.name,
          chargingModel: values.chargingModel,
          // Recipe gate retired in UI — always true so item-tab Is Recipe toggle is unblocked.
          allowsRecipe: true,
          budgetMonthly: values.budgetMonthly ?? null,
          primaryResponsibleStaff: values.primaryResponsibleStaff ?? null,
          status: values.status ?? "Active",
          notes: values.notes ?? null,
        },
      });
      toast({ title: "Category saved." });
      queryClient.invalidateQueries({ queryKey: getListInventoryCategoriesQueryKey() });
      handleClose();
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  const itemCountByCategory = (catIdNum: number) =>
    items?.filter((i) => i.categoryId === catIdNum).length ?? 0;

  return (
    <>
      <div className="mb-4 flex gap-2 items-center flex-wrap">
        <Input
          placeholder="Search categories…"
          className="w-full sm:w-[220px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant={selectMode ? "secondary" : "outline"}
          size="sm"
          onClick={toggleSelectMode}
        >
          {selectMode ? <X className="mr-2 h-4 w-4" /> : <CheckSquare className="mr-2 h-4 w-4" />}
          {selectMode ? "Cancel" : "Select"}
        </Button>
      </div>

      {selectMode && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 rounded-full border bg-background/95 pl-4 pr-2 py-1.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 max-w-[calc(100vw-2rem)]">
          <span className="text-sm font-medium pr-2 whitespace-nowrap">{selectedIds.size} selected</span>
          <div className="h-5 w-px bg-border" />
          <Button
            size="sm"
            variant="ghost"
            disabled={bulkBusy}
            onClick={() => setBulkConfirm("delete")}
            className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="mr-2 h-4 w-4" />Delete
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={bulkBusy}
            onClick={() => setBulkConfirm("inactive")}
            className="rounded-full"
          >
            <EyeOff className="mr-2 h-4 w-4" />Mark Inactive
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={bulkBusy}
            onClick={() => setBulkChargingModelOpen(true)}
            className="rounded-full"
          >
            <Tag className="mr-2 h-4 w-4" />Change Usage Model
          </Button>
          <div className="h-5 w-px bg-border ml-1" />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
            disabled={bulkBusy}
            className="rounded-full"
          >
            Clear
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Categories{" "}
            <span className="text-muted-foreground font-normal text-sm">
              {filteredCategories ? `(${filteredCategories.length}${search && categories && filteredCategories.length !== categories.length ? ` of ${categories.length}` : ""})` : ""}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {selectMode && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(c) => selectAllVisible(Boolean(c))}
                      aria-label="Select all visible categories"
                    />
                  </TableHead>
                )}
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Usage Model</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead className="text-right w-[180px] pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {selectMode && <TableCell><Skeleton className="h-4 w-4" /></TableCell>}
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                  </TableRow>
                ))
              ) : filteredCategories?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={selectMode ? 6 : 5} className="h-24 text-center text-muted-foreground">
                    No categories yet — add the first one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCategories?.map((cat) => {
                  const itemCount = itemCountByCategory(cat.id);
                  return (
                  <TableRow key={cat.id} data-state={selectedIds.has(cat.id) ? "selected" : undefined}>
                    {selectMode && (
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(cat.id)}
                          onCheckedChange={() => toggleRow(cat.id)}
                          aria-label={`Select ${cat.name}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs">{catId(cat.id)}</TableCell>
                    <TableCell>
                      <div className="font-medium">{cat.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {itemCount === 0 ? "no items" : itemCount === 1 ? "1 item" : `${itemCount} items`}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{cat.chargingModel.replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-sm">
                      {cat.budgetMonthly != null ? `₹${cat.budgetMonthly}` : "—"}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap pr-4">
                      <div className="flex justify-end items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setHistoryTarget(cat)}
                          title="View history"
                        >
                          <HistoryIcon className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(cat)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 disabled:text-muted-foreground"
                          onClick={() => setDeleteTarget(cat)}
                          disabled={itemCount > 0}
                          title={itemCount > 0
                            ? `${itemCount} item${itemCount === 1 ? "" : "s"} attached — reassign to another category before deleting`
                            : "Delete category"}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? `Edit ${catId(editId!)}` : "Add Category"}
            </DialogTitle>
          </DialogHeader>
          {isEditing ? (
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEdit)}>
                <div className="max-h-[60vh] overflow-y-auto pr-4">
                  <div className="space-y-3 p-1">
                    <FormField
                      name="name"
                      control={editForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category Name <span className="text-destructive">*</span></FormLabel>
                          <FormControl><Input placeholder="e.g. Pantry & F&B" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="chargingModel"
                      control={editForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Usage Model <span className="text-destructive">*</span></FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger><SelectValue placeholder="Pick a usage model" /></SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {CHARGING_MODELS.map((m) => (
                                <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="budgetMonthly"
                      control={editForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Budget Monthly ₹</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="Optional — monthly procurement budget"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="primaryResponsibleStaff"
                      control={editForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Responsible Staff</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Optional — e.g. Khushi"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="status"
                      control={editForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select value={field.value ?? "Active"} onValueChange={field.onChange}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Archived">Archived</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="notes"
                      control={editForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Optional"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={handleClose}>Discard changes</Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? "Saving…" : "Save Category"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          ) : (
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit(onCreate)}>
                <div className="max-h-[60vh] overflow-y-auto pr-4">
                  <div className="space-y-3 p-1">
                    <FormField
                      name="name"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category Name <span className="text-destructive">*</span></FormLabel>
                          <FormControl><Input placeholder="e.g. Pantry & F&B" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="chargingModels"
                      control={createForm.control}
                      render={() => (
                        <FormItem>
                          <FormLabel>Usage Models <span className="text-destructive">*</span></FormLabel>
                          <p className="text-xs text-muted-foreground mb-2">
                            One category gets created per checked model. So checking 2 models with name
                            "Pantry & F&B" yields 2 separate categories. Use <em>Internal Use</em> for
                            supplies the office consumes (cleaning chemicals, IT hardware, electrical)
                            that aren't surfaced to members.
                          </p>
                          <div className="space-y-2">
                            {CHARGING_MODELS.map((m) => (
                              <FormField
                                key={m}
                                name="chargingModels"
                                control={createForm.control}
                                render={({ field }) => {
                                  const checked = (field.value as string[]).includes(m);
                                  return (
                                    <FormItem className="flex items-center space-x-2 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={checked}
                                          onCheckedChange={(v) => {
                                            const current = (field.value as string[]) ?? [];
                                            field.onChange(v ? [...current, m] : current.filter((x) => x !== m));
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="font-normal text-sm">{m.replace(/_/g, " ")}</FormLabel>
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="budgetMonthly"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Budget Monthly ₹</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="Optional — monthly procurement budget"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="primaryResponsibleStaff"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Responsible Staff</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Optional — e.g. Khushi"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="status"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select value={field.value ?? "Active"} onValueChange={field.onChange}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Archived">Archived</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="notes"
                      control={createForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Optional"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={handleClose}>Discard changes</Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? "Creating…" : "Save Category"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes the category {deleteTarget && `(${deleteTarget.chargingModel.replace(/_/g, " ")})`}
              {" "}from the catalog. If items still reference it, the delete will be blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk: Delete confirmation */}
      <AlertDialog open={bulkConfirm === "delete"} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} categories?</AlertDialogTitle>
            <AlertDialogDescription>
              Categories that still have items referencing them will be skipped (FK constraint).
              A summary toast will report how many succeeded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkBusy ? "Deleting…" : `Delete ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk: Mark Inactive confirmation */}
      <AlertDialog open={bulkConfirm === "inactive"} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {selectedIds.size} categories as Inactive?</AlertDialogTitle>
            <AlertDialogDescription>
              Sets status = Archived. Categories stay in the catalog and items referencing them
              are unaffected, but they no longer surface in dropdowns by default. Reversible
              from the Edit dialog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkInactive} disabled={bulkBusy}>
              {bulkBusy ? "Updating…" : `Mark ${selectedIds.size} inactive`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk: Change Usage Model */}
      <Dialog open={bulkChargingModelOpen} onOpenChange={(o) => !o && setBulkChargingModelOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change usage model on {selectedIds.size} categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Select value={bulkChargingModel} onValueChange={(v) => setBulkChargingModel(v as ChargingModel)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHARGING_MODELS.map((m) => (
                  <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              All selected categories will be updated to this charging model. Items referencing
              them keep their existing chargingModel until manually changed.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkChargingModelOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button onClick={handleBulkChargingModel} disabled={bulkBusy}>
              {bulkBusy ? "Updating…" : `Apply to ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuditHistoryDialog
        open={historyTarget !== null}
        onOpenChange={(o) => !o && setHistoryTarget(null)}
        tableName="inventory_categories"
        recordId={historyTarget?.id ?? null}
        recordLabel={historyTarget ? `${catId(historyTarget.id)} · ${historyTarget.name}` : undefined}
      />
    </>
  );
}
